import {apiFetch} from '~/composables/api/apiFetch';
import type {IcpDefinitionResponse} from '~/types/icp';

export interface IcpDefinitionState {
  data: IcpDefinitionResponse | null;
  refresh: () => Promise<void>;
}

/**
 * Returns the global ICP definition state, initializing it on first call.
 * Pass `initialData` from the root (app.vue) to avoid a duplicate fetch on init.
 * Subsequent callers can use this without arguments to read the state.
 */
export function useIcpDefinitionState(initialData?: IcpDefinitionResponse | null) {
  const icpDefinitionState = useState<IcpDefinitionState>('icpDefinition', () => ({
    data: initialData ?? null,
    refresh: async () => {
      const fresh = (await apiFetch('/icp/definition')) as IcpDefinitionResponse | null;
      icpDefinitionState.value.data = fresh;
    },
  }));

  return icpDefinitionState;
}

/**
 * Returns the ICP definition state, fetching the value lazily if it hasn't been loaded yet.
 * Useful for pages/components that may be reached before app.vue's initial fetch has populated the state.
 */
export async function ensureIcpDefinitionState() {
  const state = useIcpDefinitionState();
  if (state.value.data === null) {
    await state.value.refresh();
  }
  return state;
}
